import { useTranslation } from 'react-i18next';
import { ExternalLink, Plus, Settings } from 'lucide-react';
import { Button, SkeletonAvatarRow } from '@shared/ui';
import type { ProjectDto } from '@shared/api/contracts';

interface ProjectListProps {
  projects: ProjectDto[];
  isLoading?: boolean;
  onEdit?: (project: ProjectDto) => void;
  onAdd?: () => void;
}

export function ProjectList({ projects, isLoading = false, onEdit, onAdd }: ProjectListProps) {
  const { t } = useTranslation('cabinet');

  if (isLoading) {
    return (
      <div className="list-stack">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="surface-card p-4 sm:p-5">
            <SkeletonAvatarRow />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="page-stack">
      <div className="surface-card flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <p className="font-sans text-sm text-foreground">{t('projects.info')}</p>
        <Button variant="default" className="page-action-button w-full shrink-0 sm:w-auto sm:max-w-xs" onClick={onAdd}>
          <Plus className="mr-2 h-4 w-4" />
          {t('projects.add')}
        </Button>
      </div>

      {!projects.length ? (
        <div className="empty-state">
          <p className="section-description">{t('projects.empty')}</p>
        </div>
      ) : (
        <div className="list-stack">
          {projects.map((project) => (
            <div key={project.id} className="surface-card">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                <h3 className="item-title">{project.name}</h3>
                <div className="flex min-w-0 items-center gap-3">
                  {project.link && (
                    <a
                      href={project.link}
                      target="_blank"
                      rel="noreferrer"
                      title={project.link}
                      className="flex min-w-0 items-center gap-2 font-sans text-xs text-primary hover:underline sm:text-sm"
                    >
                      <ExternalLink className="h-4 w-4 shrink-0" />
                      <span className="truncate">{project.link}</span>
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={() => onEdit?.(project)}
                    className="ml-auto flex size-tap shrink-0 items-center justify-center rounded-lg text-primary transition-colors duration-fast ease-standard hover:bg-secondary"
                    aria-label={t('common.edit', { ns: 'common' })}
                  >
                    <Settings className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
